import * as Contacts from 'expo-contacts';
import * as SMS from 'expo-sms';
import ArrowLeft from '@assets/icons/arrow-left.svg';
import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { AdminToggleRow } from '@/components/restaurant/admin-toggle-row';
import { APP_MODAL_ROUTES, ORDER_INBOUNDS_ROUTES } from '@/navigators/routes';
import {
  AppModalStackParams,
  OrderInboundsStackParams,
  RestaurantInviteEmployeeProps,
} from '@/navigators/types';
import { Button } from '@/components/button';
import { ContactsPermissionActionSheet } from '@/components/contacts-permission-action-sheet';
import {
  ControlledPhoneNumberInput,
  usePhoneNumberState,
} from '@/components/phone-number-input';
import { createStyles } from '@/contexts/theme-context';
import { EmailInput } from '@/components/email-input';
import { formatPhoneNumber } from '@/utils/phone-number';
import { FormInputContainer } from '@/components/form';
import { KeyboardAwareScrollView } from '@/components/with-keyboard-aware';
import {
  MatchingAccountActionSheet,
  SelectedMatchingAccountType,
} from './matching-account-action-sheet';
import { ModalContainer } from '@/components/modal-container';
import { Props as NavigationHeaderProps } from '@/components/navigation-header';
import { partnerSupplier } from '@/utils/environment';
import { Platform, Text, View } from 'react-native';
import { SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { StackNavigationProp } from '@react-navigation/stack';
import { TextInput } from '@/components/text-input';
import { Type } from '@/components/toast';
import { useEmployeeInvite } from '@/hooks/use-employee-invite';
import { usePermissionDeniedAlert } from '@/hooks/use-permission-denied-alert';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useToasts } from '@/contexts/toast-context';

export const RestaurantInviteEmployee: React.FC<
  RestaurantInviteEmployeeProps & {
    navigation: StackNavigationProp<
      OrderInboundsStackParams | AppModalStackParams,
      ORDER_INBOUNDS_ROUTES.Submit | APP_MODAL_ROUTES.RestaurantInviteEmployee
    >;
    navigationHeaderProps?: NavigationHeaderProps;
  }
> = ({
  initialEmailAddress,
  onComplete,
  initialPhoneNumber,
  navigation,
  navigationHeaderProps,
  restaurantUUID,
  restaurantName,
  restaurantRelationship,
  supplierUUID,
}) => {
  const { bottom } = useSafeAreaInsets();
  const [styles] = useStyles();

  const [showPermissionSheet, setShowPermissionSheet] = useState(false);
  const [showMatchingAccountActionSheet, setShowMatchingAccountActionSheet] =
    useState(false);
  const [name, setName] = useState('');

  const [validatedPhoneNumber, setValidatedPhoneNumber] = useState<
    string | null
  >(initialPhoneNumber ?? null);
  const [contactEmail, setContactEmail] = useState(initialEmailAddress ?? '');
  const [loginEmail, setLoginEmail] = useState(initialEmailAddress ?? '');
  const [isAdmin, setAdmin] = useState(false);
  const [hasLoginDetailError, setHasLoginDetailError] = useState(false);
  const { showErrorToast, showToast } = useToasts();

  const {
    phoneNumber: rawPhoneNumber,
    selectedCountryCode,
    setPhoneNumber: setRawPhoneNumber,
    setSelectedCountryCode,
  } = usePhoneNumberState(initialPhoneNumber ?? '', setValidatedPhoneNumber);

  const showPermissionDeniedAlert = usePermissionDeniedAlert();

  const { handleInviteEmployee, loading } = useEmployeeInvite({
    contactEmail,
    handleNotification: async ({
      invitationResponse,
      selectedMatchingAccount,
      targetRestaurantRelationship,
    }) => {
      const marketplaceURL =
        invitationResponse?.inviteRestaurantEmployee.pepper_url || null;

      const universalURL =
        invitationResponse?.inviteRestaurantEmployee.whitelabel_universal_url;
      const androidURL =
        invitationResponse?.inviteRestaurantEmployee.whitelabel_android_url ||
        marketplaceURL;
      const iosURL =
        invitationResponse?.inviteRestaurantEmployee.whitelabel_ios_url ||
        marketplaceURL;

      const paymentsOnlyEnabled =
        targetRestaurantRelationship.paymentsOnlyEnabled;

      let message;
      if (universalURL && !paymentsOnlyEnabled) {
        message = i18n.t('invite_employee_sms.whitelabel_universal', {
          appName: partnerSupplier.fullName,
          restaurantName,
          universalURL,
        });
      } else if (universalURL && paymentsOnlyEnabled) {
        message = i18n.t(
          'invite_employee_sms.whitelabel_universal_payments_only',
          {
            appName: partnerSupplier.fullName,
            restaurantName,
            universalURL,
          },
        );
      } else {
        message = i18n.t('invite_employee_sms.whitelabel', {
          androidURL,
          appName: partnerSupplier.fullName,
          iosURL,
          restaurantName,
        });
      }

      try {
        if (
          validatedPhoneNumber &&
          selectedMatchingAccount !== SelectedMatchingAccountType.EMAIL
        ) {
          await SMS.sendSMSAsync(validatedPhoneNumber, message);
        } else if (!selectedMatchingAccount) {
          showToast({
            autoCloseTimeout: 8000, // 8 seconds to read the message before it auto-closes
            content: (
              <Text style={TYPOGRAPHY.toast}>
                {i18n.t('restaurant.invite_employee_message', {
                  name,
                  universalURL,
                })}
              </Text>
            ),
            type: Type.POSITIVE,
          });
        }
      } catch (smsError) {
        // ignore sms error
      }
    },
    hasInviteConflictError: () => setShowMatchingAccountActionSheet(true),
    isAdmin,
    loginEmail,
    name,
    onClose: () => navigation.goBack(),
    onComplete,
    restaurantName,
    restaurantRelationship,
    restaurantUUID,
    setHasLoginDetailError,
    supplierUUID,
    validatedPhoneNumber,
  });

  const handleMatchingAccountActionSheetClose = async (
    selectedValue?: SelectedMatchingAccountType,
  ) => {
    setShowMatchingAccountActionSheet(false);
    if (selectedValue) {
      await handleInviteEmployee(selectedValue);
    }
  };

  const navigateToContactList = () => {
    navigation.navigate(APP_MODAL_ROUTES.ContactSelector, {
      onContactPhoneNumberSelect: (
        contactName: string,
        contactNumber: string,
        emailAddress: string | null,
      ) => {
        setName(contactName);
        setValidatedPhoneNumber(contactNumber);
        setRawPhoneNumber(formatPhoneNumber(contactNumber));
        if (emailAddress) {
          setContactEmail(emailAddress);
        }
      },
    });
  };

  const backButtonProps = supplierUUID
    ? {
        BackButtonIcon: ArrowLeft,
        showBackButton: true,
      }
    : {};

  return (
    <>
      <ModalContainer
        navigationHeaderProps={{
          ...backButtonProps,
          title: i18n.t('invite_employee'),
          ...navigationHeaderProps,
        }}
        shouldDismissKeyboard
      >
        <KeyboardAwareScrollView
          alwaysBounceVertical={false}
          contentContainerStyle={[
            styles.scrollView,
            { paddingBottom: Math.max(bottom, SCREEN_PADDING) },
          ]}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.base}>
            <FormInputContainer>
              <TextInput
                autoCapitalize="words"
                autoCorrect={false}
                containerStyle={styles.inputContainer}
                label={name ? i18n.t('name') : null}
                onChangeText={setName}
                placeholder={`${i18n.t('name')}*`}
                textContentType="name"
                value={name}
              />
            </FormInputContainer>
            <FormInputContainer>
              <ControlledPhoneNumberInput
                containerStyle={[
                  styles.inputContainer,
                  styles.phoneNumberContainer,
                ]}
                error={
                  hasLoginDetailError ? i18n.t('missing_login_details') : null
                }
                label={rawPhoneNumber ? i18n.t('mobile_number') : null}
                onChangeText={setRawPhoneNumber}
                onCountryCodeChange={setSelectedCountryCode}
                placeholder={`${i18n.t('mobile_number')}`}
                selectedCountryCode={selectedCountryCode}
                value={rawPhoneNumber || ''}
              />
            </FormInputContainer>
            <FormInputContainer>
              <EmailInput
                containerStyle={styles.inputContainer}
                error={
                  hasLoginDetailError ? i18n.t('missing_login_details') : null
                }
                label={i18n.t('login_email')}
                onChangeText={setLoginEmail}
                placeholder={i18n.t('login_email')}
                value={loginEmail}
              />
            </FormInputContainer>
            <FormInputContainer>
              <EmailInput
                containerStyle={styles.inputContainer}
                label={i18n.t('communication_email')}
                onChangeText={setContactEmail}
                placeholder={i18n.t('email')}
                value={contactEmail}
              />
            </FormInputContainer>
            <FormInputContainer>
              <AdminToggleRow
                listItemStyle={styles.adminToggleWrapper}
                onValueChange={setAdmin}
                testID="role-toggle"
                trackingData={{
                  name: 'invite_employee.admin_toggle',
                  properties: {
                    isAdmin: !isAdmin,
                  },
                }}
                value={isAdmin}
              />
            </FormInputContainer>
            {Platform.OS !== 'web' ? (
              <Button
                onPress={async () => {
                  try {
                    const { status, canAskAgain, granted } =
                      await Contacts.getPermissionsAsync();
                    if (status === Contacts.PermissionStatus.GRANTED) {
                      navigateToContactList();
                    } else if (!canAskAgain && !granted) {
                      showPermissionDeniedAlert(
                        i18n.t('contacts_permission.denied_alert'),
                      );
                    } else {
                      setShowPermissionSheet(true);
                    }
                  } catch (e) {
                    showErrorToast();
                  }
                }}
                trackingData={{ name: 'invite_employee.contact_list_button' }}
                type="tertiary"
              >
                {i18n.t('invite_employee.contact_list')}
              </Button>
            ) : null}
          </View>
          <Button
            containerStyle={styles.buttonContainer}
            disabled={
              !name.trim() ||
              (!validatedPhoneNumber && !contactEmail && !loginEmail)
            }
            loading={loading}
            onPress={handleInviteEmployee}
            testID="invite-employee-btn"
            trackingData={{ name: 'invite_employee.submit_button' }}
          >
            {i18n.t('invite_employee')}
          </Button>
        </KeyboardAwareScrollView>
      </ModalContainer>
      {showPermissionSheet ? (
        <ContactsPermissionActionSheet
          onClose={() => setShowPermissionSheet(false)}
          onPermissionGranted={navigateToContactList}
        />
      ) : null}
      {showMatchingAccountActionSheet ? (
        <MatchingAccountActionSheet
          contactInfo={{
            loginEmail,
            phoneNumber: formatPhoneNumber(validatedPhoneNumber!),
          }}
          onClose={handleMatchingAccountActionSheetClose}
        />
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  adminToggleWrapper: {
    paddingHorizontal: 0,
    paddingTop: 8,
  },
  base: {
    marginBottom: 16,
  },
  buttonContainer: {
    marginTop: 16,
  },
  inputContainer: {
    borderColor: theme.colors.stroke,
  },
  phoneNumberContainer: {
    borderColor: theme.colors.stroke,
  },
  scrollView: {
    flexGrow: 1,
    justifyContent: 'space-between',
    paddingHorizontal: SCREEN_PADDING,
  },
}));
